import { createApi } from '@reduxjs/toolkit/query/react';
import { ILoginFormValues } from '../Interfaces/formValue-interfaces';
import { IChangePasswordData, ICheckVerifyCode, ICreateBusinessData, ICreateCategory, ICreateMenuItem, IEditUserData, IGetCompanyById, IGetCompanyFileredData, IGetMenuCategories, IMyOrderByIdRequest, IMyOrderRequest, IMyServicesRequestData, INewOrder, IRegisterFormValues, IRemoveCategory, IRemoveImageData, ISendVerifyCode, IUpdateCategory, IUpdateCompanyById, IUpdateMenuItem, IUpdateMenuItemActive, IUpdateMenuItemOrderList, IUpdateOrderStatus, } from '../Interfaces/request-interfaces';
import axiosBaseQuery from './API';
import { GetMyCompaniesResponse, ICategory, ICompanyCurrentOrdersResponce, ICompanyResponse, ILoginResponse, IMenuItemResponce, IMyOrdersResponse, IOrderResponse, IResendSmsResponse, IUploadImageResponse, IVerifyCodeResponse, MenuListResponce } from '../Interfaces/responseTypes';
import { AxiosRequestHeaders } from 'axios';
import {
  parseCompanyPosTerminal,
  readCompanyPosTerminalFromStorage,
  setCompanyPosTerminalStorage,
} from '../Utilits/utilits';

export const authApi = createApi({
  reducerPath: 'authApi',
  tagTypes: ['Users', 'Companies', 'MyOrders', 'Company_Orders', 'CompanyDetail'],
  baseQuery: axiosBaseQuery({
    baseUrl: process.env.REACT_APP_API_KEY,
  }),
  endpoints: (builder) => ({
    login: builder.mutation<ILoginResponse, ILoginFormValues>({
      query: (credentialsLogin) => ({
        url: '/api/login_user/',
        method: 'POST',
        data: credentialsLogin,
      })
    }),
    register: builder.mutation<any, IRegisterFormValues>({
      query: (credentials) => ({
        url: '/api/register_user/',
        method: 'POST',
        data: credentials,
      }),
    }),
    sendVerifyCode: builder.mutation<any, ISendVerifyCode>({
      query: (credentials) => ({
        url: '/api/send_code_to_email/',
        method: 'POST',
        data: credentials,
      }),
    }),
    checkVerifyCode: builder.mutation<any, ICheckVerifyCode>({
      query: (credentials) => ({
        url: '/api/check_code_from_email/',
        method: 'POST',
        data: credentials,
      }),
    }),
    editUserData: builder.mutation<any, IEditUserData>({
      query: (credentials) => ({
        url: '/api/update_profile/',
        method: 'PUT',
        data: credentials,
      }),
    }),
    changePassword: builder.mutation<any, IChangePasswordData>({
      query: (credentials) => ({
        url: '/api/change_password/',
        method: 'POST',
        data: credentials,
      }),
    }),
    uploadImage: builder.mutation<IUploadImageResponse, FormData>({
      query: (credentials) => ({
        url: '/api/image/upload/',
        method: 'POST',
        data: credentials,
      }),
    }),
    removeImage: builder.mutation<void, IRemoveImageData>(
      {
        query: ({ image_src, company_id }) => ({
          url: `/api/image/remove/?image_src=${encodeURIComponent(image_src)}&company_id=${company_id}`,
          method: 'DELETE',
        }),
      }
    ),
    createCompany: builder.mutation<ICompanyResponse, ICreateBusinessData>({
      query: (credentials) => ({
        url: '/api/company/create/',
        method: 'POST',
        data: credentials,
      }),
      invalidatesTags: ['Companies']
    }),
    getMyCompanies: builder.query<GetMyCompaniesResponse, void>({
      query: () => ({
        url: `/api/company`,
        method: 'GET',
      }),
      providesTags: ['Companies']
    }),
    getCompanyById: builder.query<ICompanyResponse, IGetCompanyById>({
      query: ({ id, new_visitor }) => ({
        url: `/api/company/${id}/?new_visitor=${new_visitor ?? false}`,
        method: 'GET',
      }),
      serializeQueryArgs: ({ queryArgs }) => queryArgs.id,
      providesTags: (result, error, { id }) => [{ type: 'CompanyDetail', id }],
      transformResponse: (response: ICompanyResponse) => {
        const fromApi = parseCompanyPosTerminal(response);
        const fromStorage =
          typeof response.id === 'number'
            ? readCompanyPosTerminalFromStorage(response.id)
            : null;

        let pos_terminal = response.pos_terminal;
        if (fromApi === true) {
          pos_terminal = true;
        } else if (fromApi === false && fromStorage === true) {
          pos_terminal = true;
        } else if (fromApi === null && fromStorage !== null) {
          pos_terminal = fromStorage;
        } else if (fromApi === false) {
          pos_terminal = false;
        }

        return {
          ...response,
          pos_terminal: pos_terminal ?? response.pos_terminal ?? false,
        };
      },
    }),
    removeCompany: builder.mutation<any, number>({
      query: (id) => ({
        url: `/api/company/${id}/delete/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Companies']
    }),
    updateCompanyById: builder.mutation<ICompanyResponse, IUpdateCompanyById>({
      query: ({ id, data }) => ({
        url: `/api/company/${id}/update/`,
        method: 'PUT',
        data: data,
      }),
      async onQueryStarted({ id, data }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          authApi.util.updateQueryData('getCompanyById', { id }, (draft) => {
            if (data.cost_currency !== undefined) {
              draft.cost_currency = data.cost_currency;
            }
            if (data.service_fee !== undefined) {
              draft.service_fee = data.service_fee;
            }
            if (data.pos_terminal !== undefined) {
              draft.pos_terminal = data.pos_terminal;
            }
          })
        );

        try {
          const { data: updatedCompany } = await queryFulfilled;

          if (data.pos_terminal !== undefined) {
            setCompanyPosTerminalStorage(id, data.pos_terminal ?? false);
          }

          dispatch(
            authApi.util.updateQueryData('getCompanyById', { id }, (draft) => {
              Object.assign(draft, updatedCompany);
              if (data.cost_currency !== undefined) {
                draft.cost_currency = data.cost_currency;
              }
              if (data.service_fee !== undefined) {
                draft.service_fee = data.service_fee;
              }
              if (data.pos_terminal !== undefined) {
                draft.pos_terminal = data.pos_terminal;
              }
            })
          );
        } catch {
          patchResult.undo();
        }
      },
    }),
    getMenuCtegories: builder.query<ICategory[], IGetMenuCategories>({
      query: ({ business_type, company_id }) => ({
        url: `/api/category/${business_type}?company_id=${company_id}`,
        method: 'GET',
      }),
    }),
    getMenuList: builder.query<MenuListResponce, number>({
      query: (company_id) => ({
        url: `/api/company/${company_id}/menu_list/`,
        method: 'GET',
      }),
    }),
    createCategory: builder.mutation<ICategory, ICreateCategory>({
      query: (credentials) => ({
        url: '/api/category/create/',
        method: 'POST',
        data: credentials,
      }),
    }),
    updateCategory: builder.mutation<any, IUpdateCategory>({
      query: (credentials) => ({
        url: '/api/category/update/',
        method: 'PUT',
        data: credentials,
      }),
    }),
    removeCategory: builder.mutation<any, IRemoveCategory>({
      query: (credentials) => ({
        url: `/api/category/delete/`,
        method: 'DELETE',
        data: credentials
      }),
    }),
    createMenuItem: builder.mutation<IMenuItemResponce, ICreateMenuItem>({
      query: (credentials) => ({
        url: '/api/category/crete_menu_item/',
        method: 'POST',
        data: credentials,
      }),
    }),
    updateMenuItem: builder.mutation<any, IUpdateMenuItem>({
      query: (credentials) => ({
        url: '/api/category/update_menu_item/',
        method: 'PUT',
        data: credentials,
      }),
    }),
    updateMenuItemActivity: builder.mutation<any, IUpdateMenuItemActive>({
      query: (credentials) => ({
        url: '/api/category/update_menu_item/',
        method: 'PUT',
        data: credentials,
      }),
    }),
    updateMenuOrderList: builder.mutation<any, IUpdateMenuItemOrderList>({
      query: (credentials) => ({
        url: '/api/category/menu_item_order_list/',
        method: 'POST',
        data: credentials,
      }),
    }),

    removeMenuItem: builder.mutation<any, number>({
      query: (id) => ({
        url: `/api/category/delete_menu_item/${id}/`,
        method: 'DELETE',
      }),
    }),
    createServices: builder.mutation<any, IMyServicesRequestData>({
      query: ({ id, body }) => ({
        url: `/api/company/${id}/create_services/`,
        method: 'POST',
        data: body,
      }),
    }),
    updateServices: builder.mutation<any, IMyServicesRequestData>({
      query: ({ id, body }) => ({
        url: `/api/company/${id}/update_services/`,
        method: 'PUT',
        data: body,
      }),
    }),
    createOrder: builder.mutation<any, INewOrder>({
      query: (credentials) => ({
        url: `/api/order/new/`,
        method: 'POST',
        data: credentials,
      }),
      invalidatesTags: ['MyOrders']
    }),
    verifySms: builder.mutation<IVerifyCodeResponse, INewOrder>({
      query: (credentials) => ({
        url: `/api/order/check_sms/`,
        method: 'POST',
        data: credentials,
      }),
    }),
    getMyOrders: builder.query<IMyOrdersResponse, IMyOrderRequest>({
      query: ({ phone, page = 1, user_uuid }) => ({
        url: `/api/order/my?phone=${phone}&page=${page}&user_uuid=${user_uuid}`,
        method: 'GET',
        _withTimeZone: true
      }),
      providesTags: ['MyOrders']
    }),
    getMyOrderById: builder.query<IOrderResponse, IMyOrderByIdRequest>({
      query: ({ id, phone, user_uuid }) => ({
        url: `/api/order/${id}?phone=${phone}&user_uuid=${user_uuid}`,
        method: 'GET',
        _withTimeZone: true
      }),
      providesTags: ['MyOrders']
    }),
    getCompanyOrders: builder.query<ICompanyCurrentOrdersResponce, { id: number }>({
      query: ({ id }) => ({
        url: `/api/order/company/${id}/`,
        method: 'GET',
        _withTimeZone: true,
      }),
      providesTags: ['Company_Orders'],
    }),
    getCompanyOrderById: builder.query<IOrderResponse, {
      order_id: string,
      company_id: number
    }>({
      query: ({ order_id, company_id }) => ({
        url: `/api/order/${order_id}?company_id=${company_id}`,
        method: 'GET',
        _withTimeZone: true,
      }),
      providesTags: ['Company_Orders'],
    }),
    getCompanyFilteredOrders: builder.query<any, IGetCompanyFileredData>({
      query: ({ company_id, start_date, end_date, page, order_type, order_status, location_type, location_number, today }) => {
        const params = new URLSearchParams();

        if (start_date) params.append("start_date", start_date);
        if (end_date) params.append("end_date", end_date);
        if (page) params.append("page", page.toString());
        if (order_type) params.append("order_type", order_type);
        if (order_status) params.append("order_status", order_status);
        if (location_type) params.append("location_type", location_type);
        if (location_number != null) params.append("location_number", location_number.toString());
        if (today !== undefined) params.append("today", today.toString());

        return {
          url: `/api/order/company/${company_id}/?${params.toString()}`,
          method: "GET",
          _withTimeZone: true
        };
      },
      providesTags: ["Company_Orders"],
    }),
    updateOrderStatus: builder.mutation<any, IUpdateOrderStatus>({
      query: (credentials) => ({
        url: `/api/order/update_status/${credentials.order_id}/`,
        method: 'POST',
        data: credentials.body
      }),
      invalidatesTags: ['Company_Orders']
    }),
    resendSms: builder.mutation<IResendSmsResponse, { phone_number: string }>({
      query: (credentials) => ({
        url: `/api/order/resend_sms/`,
        method: 'POST',
        data: credentials
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useCheckVerifyCodeMutation,
  useSendVerifyCodeMutation,
  useEditUserDataMutation,
  useChangePasswordMutation,
  useUploadImageMutation,
  useRemoveImageMutation,
  useCreateCompanyMutation,
  useGetMyCompaniesQuery,
  useGetCompanyByIdQuery,
  useRemoveCompanyMutation,
  useUpdateCompanyByIdMutation,
  useGetMenuCtegoriesQuery,
  useGetMenuListQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useRemoveCategoryMutation,
  useCreateMenuItemMutation,
  useUpdateMenuItemMutation,
  useUpdateMenuItemActivityMutation,
  useRemoveMenuItemMutation,
  useUpdateMenuOrderListMutation,
  useCreateServicesMutation,
  useUpdateServicesMutation,
  useCreateOrderMutation,
  useVerifySmsMutation,
  useGetMyOrdersQuery,
  useGetMyOrderByIdQuery,
  useGetCompanyOrdersQuery,
  useUpdateOrderStatusMutation,
  useGetCompanyFilteredOrdersQuery,
  useLazyGetCompanyFilteredOrdersQuery,
  useLazyGetCompanyOrderByIdQuery,
  useResendSmsMutation,
} = authApi;
